import React, {
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

import SurveyContext from '@totem/components/surveyV2/instanceDetail/SurveyInstanceContext';
import {
  QuestionValue,
  RequestSurveyInstanceQuestionAnswer,
  SurveyError,
  SurveyQueryResult,
} from '@totem/components/surveyV2/types';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { V2_SURVEY_ENDPOINT } from '@totem/utilities/endpoints';
type Props = {
  surveyInstanceId: string;
  children?: ReactNode;
};
const SurveyInstanceContainer = ({ surveyInstanceId, children }: Props) => {
  const { getApiToken } = useContext(UserProfileContext);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [refresh, setRefresh] = useState<boolean>(true);
  const [data, setData] = useState<SurveyQueryResult>(null);
  const [errorAlertVisible, setErrorAlertVisible] = useState<boolean>(false);
  const [errors, setErrors] = useState<SurveyError[]>([]);
  const [filterAnswered, setFilterAnswered] = useState<boolean>(false);
  const questionAbortControllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    if (refresh) {
      setRefresh(false);

      setIsLoading(true);

      fetch(`${V2_SURVEY_ENDPOINT}/instance/${surveyInstanceId}`, {
        method: 'GET',
        headers: new Headers({
          Authorization: `Bearer ${getApiToken()}`,
        }),
      })
        .then((res) => res.json())
        .then((result: SurveyQueryResult) => {
          setData(result);
        })
        .then(() => {
          setIsLoading(false);
        });
    }
  }, [surveyInstanceId, refresh, getApiToken]);

  const sendQuestionAnswer = useCallback(
    async (
      surveyId: string,
      categoryId: string,
      questionId: string,
      newQuestionValue: QuestionValue,
      newAdditionalContext: string,
    ) => {
      if (questionAbortControllerRef.current) {
        questionAbortControllerRef.current.abort();
      }

      const abortController = new AbortController();
      questionAbortControllerRef.current = abortController;

      const request: RequestSurveyInstanceQuestionAnswer = {
        surveyInstanceId: surveyId,
        categoryId,
        questionId,
        answer: newQuestionValue,
        additionalContext: newAdditionalContext,
      };

      fetch(`${V2_SURVEY_ENDPOINT}/instance/question`, {
        method: 'POST',
        headers: new Headers({
          Authorization: `Bearer ${getApiToken()}`,
        }),
        body: JSON.stringify(request),
        signal: abortController.signal,
      })
        .then((res) => res.json())
        .then((result: SurveyQueryResult) => {
          setData(result);
        })
        .then(() => {
          setIsLoading(false);
        })
        .catch((err) => {
          if (err.name !== 'AbortError') {
            throw err;
          }
        });
    },
    [],
  );

  const sendNewCategoryInstanceRequest = useCallback(
    async (surveyId: string, categoryId: string, displayName: string) => {
      const payload = { displayName };

      fetch(
        `${V2_SURVEY_ENDPOINT}/instance/${surveyId}/categories/${categoryId}/instance`,
        {
          method: 'POST',
          headers: new Headers({
            Authorization: `Bearer ${getApiToken()}`,
          }),
          body: JSON.stringify(payload),
        },
      )
        .then((res) => res.json())
        .then((result: SurveyQueryResult) => {
          setData(result);
        })
        .then(() => {
          setIsLoading(false);
        });
    },
    [],
  );

  const sendDeleteCategoryInstanceRequest = useCallback(
    async (surveyId: string, categoryId: string, instanceNumber: number) => {
      fetch(
        `${V2_SURVEY_ENDPOINT}/instance/${surveyId}/categories/${categoryId}/instance/${instanceNumber}`,
        {
          method: 'DELETE',
          headers: new Headers({
            Authorization: `Bearer ${getApiToken()}`,
          }),
        },
      )
        .then((res) => res.json())
        .then((result: SurveyQueryResult) => {
          setData(result);
        })
        .then(() => {
          setIsLoading(false);
        });
    },
    [],
  );

  const handleOnAction = (newAction: string) => {
    switch (newAction) {
      case 'refresh':
        setRefresh(true);
        break;
      default:
        break;
    }
  };

  const createCategoryInstance = (
    surveyId: string,
    categoryId: string,
    displayName: string,
  ) => {
    sendNewCategoryInstanceRequest(surveyId, categoryId, displayName);
  };

  const deleteCategoryInstance = (
    surveyId: string,
    categoryId: string,
    instanceNumber: number,
  ) => {
    sendDeleteCategoryInstanceRequest(surveyId, categoryId, instanceNumber);
  };

  const sendSubmitReopen = useCallback(
    async (surveyId: string, action: string) => {
      const payload = {};
      fetch(`${V2_SURVEY_ENDPOINT}/instance/${surveyId}/${action}`, {
        method: 'POST',
        headers: new Headers({
          Authorization: `Bearer ${getApiToken()}`,
        }),
        body: JSON.stringify(payload),
      })
        .then((res) => res.json())
        .then((result: SurveyQueryResult) => {
          setData(result);
        });
    },
    [],
  );

  const submit = (surveyId: string, action: string) => {
    sendSubmitReopen(surveyId, action);
  };

  const updateQuestionAnswer = (
    categoryId: string,
    questionId: string,
    newQuestionValue: QuestionValue,
    newAdditionalContext: string,
  ) => {
    sendQuestionAnswer(
      surveyInstanceId,
      categoryId,
      questionId,
      newQuestionValue,
      newAdditionalContext,
    );
  };

  return (
    <SurveyContext.Provider
      value={{
        surveyInstanceId,
        loading: isLoading,
        setIsLoading,
        data,
        setData,
        updateQuestionAnswer,
        createCategoryInstance,
        deleteCategoryInstance,
        submit,
        errorAlertVisible,
        setErrorAlertVisible,
        errors,
        setErrors,
        filterAnswered,
        setFilterAnswered,
        onAction: handleOnAction,
        isAuthenticated: true,
      }}
    >
      {children}
    </SurveyContext.Provider>
  );
};
export default SurveyInstanceContainer;
